import { formatMoney } from "@/lib/orderUtils";
import { badgeWithinWindow } from "@/lib/brandBadges";

/**
 * Single promo / marketing badge shown on a brand card.
 * Auto-renders from a backend Badge object: { type, min_spend, value, label }
 */
export const BadgeLabel = ({ badge }) => {
    if (!badge) return null;
    const type = badge.type || "marketing";
    // Auto-generated default text if admin didn't provide a custom label
    const auto = (() => {
        switch (type) {
            case "free_delivery": return "Free delivery";
            case "pct_off":       return `${Math.round(badge.value || 0)}% off`;
            case "amount_off":    return `${formatMoney(badge.value || 0)} off`;
            case "marketing":     return badge.label || "Featured";
            default:              return badge.label || "";
        }
    })();
    const reward = badge.label || auto;
    const minSpend = Number(badge.min_spend || 0);

    // Marketing = single green pill (no spend gate). Otherwise = stacked
    // "Spend £X" white pill + red reward banner (Just Eat-style).
    if (type === "marketing" || minSpend === 0) {
        return (
            <div
                className="self-start max-w-full pointer-events-none"
                data-testid={`brand-badge-${type}`}
            >
                <span className={`block px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.14em] font-bold shadow-sm truncate ${
                    type === "marketing"
                        ? "bg-[#19a463] text-white"
                        : "bg-[#e1262b] text-white"
                }`}>
                    {reward}
                </span>
            </div>
        );
    }
    return (
        <div className="flex flex-col items-start gap-0 pointer-events-none" data-testid={`brand-badge-${type}`}>
            <span className="bg-white text-[#0a0a0a] px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.18em] font-bold shadow-sm">
                Spend {formatMoney(minSpend)}
            </span>
            <span className="bg-[#e1262b] text-white px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.14em] font-bold shadow-sm">
                {`Get ${reward}`}
            </span>
        </div>
    );
};

/**
 * Top-left stack of badges + bottom-right ETA bubble overlaid on a brand image.
 * Should be rendered INSIDE a position:relative container, on top of the photo.
 * `orderType` (optional, "delivery"|"collection") filters out badges restricted
 * to the opposite order type so the customer only sees offers relevant to them.
 */
export const BrandBadgeOverlay = ({ badges = [], etaMin = 0, layout = "card", orderType = null }) => {
    const active = (badges || []).filter((b) => {
        if (!b || b.active === false) return false;
        const at = (b.applies_to || "both").toLowerCase();
        if (orderType && at !== "both" && at !== orderType) return false;
        return badgeWithinWindow(b);
    });
    const showEta = Number(etaMin) > 0;
    if (active.length === 0 && !showEta) return null;
    const stackPos = layout === "card" ? "top-2 left-2" : "top-3 left-3";
    const etaPos = layout === "card" ? "bottom-2 right-2" : "bottom-3 right-3";
    return (
        <>
            {active.length > 0 && (
                <div
                    className={`absolute ${stackPos} z-10 flex flex-col items-start gap-1 max-w-[70%]`}
                    data-testid="brand-badge-stack"
                >
                    {active.map((b) => <BadgeLabel key={b.id || b.label || b.type} badge={b} />)}
                </div>
            )}
            {showEta && (
                <div
                    className={`absolute ${etaPos} z-10 bg-white text-[#0a0a0a] rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.14em] font-bold shadow`}
                    data-testid="brand-eta-bubble"
                >
                    {etaMin} min
                </div>
            )}
        </>
    );
};

export default BrandBadgeOverlay;
